import ReactDOM from 'react-dom/client';
import './index.scss';

import SuperTokens from 'supertokens-auth-react';
import { SuperTokensConfig } from './authConfig';
import updateLocale from 'dayjs/plugin/updateLocale';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
import { analytics } from './analytics/analytics';
import * as Sentry from '@sentry/react';
import App from './App';

analytics.initialize();

SuperTokens.init(SuperTokensConfig);

// Only initialize Sentry in staging/production, not local development
const isLocalDevelopment = window.location.hostname === 'localhost';
if (!isLocalDevelopment) {
  const isStaging = window.location.hostname.includes('staging');
  Sentry.init({
    dsn: 'https://3a8f35f3446e7639944ea46ef6485de8@o4509469099294720.ingest.de.sentry.io/4509469100605520',
    environment: isStaging ? 'staging' : 'production',
    sendDefaultPii: true,
    // Microsoft's Outlook SafeLinks scanner opens emailed links in a headless browser and
    // rejects with this from its own injected script, never from our code.
    ignoreErrors: [/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
  });
}

dayjs.extend(updateLocale);
dayjs.extend(relativeTime);
dayjs.updateLocale('en', {
  weekStart: 1,
});

// #1346: Recover from stale code-split chunks after a deploy.
// A tab left open across a deploy (e.g. over the weekend) still references the
// previous build's hashed chunk URLs. When a route is lazily imported those
// chunks 404, the dynamic import rejects, and with no error boundary the app
// renders a blank white screen until a manual refresh. Reload once to pull the
// new index.html + chunks. A short debounce guards against reload loops if the
// asset is genuinely unavailable.
const recoverFromStaleChunk = (reason: string) => {
  const KEY = 'taskonio:lastChunkReload';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // already tried very recently — avoid a loop
  sessionStorage.setItem(KEY, String(Date.now()));
  console.warn(`Reloading to recover from stale chunk (${reason})`);
  window.location.reload();
};

// Vite dispatches this when a preloaded/code-split module fails to load.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  recoverFromStaleChunk('vite:preloadError');
});

// Fallback: dynamic-import failures can also surface as unhandled rejections.
window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  if (/dynamically imported module|module script failed|ChunkLoadError|Failed to fetch dynamically/i.test(message)) {
    recoverFromStaleChunk('unhandledrejection');
  }
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  // <React.StrictMode>
  <App />
  // </React.StrictMode>,
);
